import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataGridView } from "@neumetric/blueprint-lib/components";
import { Pagination } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { riskActions, getReferenceList } from "../../store";

const PER_PAGE = Number(process.env.REACT_APP_INC_PER_PAGE || 9);
const FOOTER_H = 48;

const columns = [
  {
    dataIndex: "name",
    title: "Reference",
    typeRepresentation: "LINK",
    width: "100%",
    showTooltip: true,
  },
];

const getPage = (search) => {
  const params = new URLSearchParams(search);
  const p = Number(params.get("page") || 1);
  return Number.isNaN(p) || p < 1 ? 1 : p;
};

export const ReferencesTrackerScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const referencesList = useSelector(getReferenceList) || [];
  const totalCount = referencesList.length;

  const pageNo = getPage(location.search);
  const maxPage = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  const safePageNo = Math.min(pageNo, maxPage);

  const rootRef = React.useRef(null);
  const [rootHeight, setRootHeight] = React.useState(500);

  React.useEffect(() => {
    dispatch(riskActions.fetchReferences());
  }, [dispatch]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("page")) {
      navigate(`${location.pathname}?page=1`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  React.useLayoutEffect(() => {
    const compute = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      const available = window.innerHeight - top - 12;
      setRootHeight(Math.max(220, available));
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const pagedReferences = React.useMemo(() => {
    const start = (safePageNo - 1) * PER_PAGE;
    return referencesList
      .slice(start, start + PER_PAGE)
      .map((r) => ({ ...r, key: r.id }));
  }, [referencesList, safePageNo]);

  const linkById = React.useMemo(() => {
    const m = new Map();
    referencesList.forEach((r) => m.set(String(r.id), r.link));
    return m;
  }, [referencesList]);

  const handleGridClickCapture = (e) => {
    // only intercept clicks inside LINK cells
    const td = e.target.closest?.("td.nm-datagridview-link-cell");
    if (!td) return;

    const tr = e.target.closest?.("tr[data-row-key]");
    const rowKey = tr?.getAttribute?.("data-row-key");
    const link = rowKey ? linkById.get(String(rowKey)) : null;

    if (!link) return;

    // stop blueprint's default same-tab navigation
    e.preventDefault?.();
    e.stopPropagation?.();
    e.nativeEvent?.stopImmediatePropagation?.();

    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handlePageChange = (p) => {
    navigate(`${location.pathname}?page=${p}`);
  };

  const showFooter = totalCount > PER_PAGE;

  return (
    <div
      ref={rootRef}
      className="col w-100 w-500-px mt-10"
      style={{ height: rootHeight, display: "flex", flexDirection: "column" }}
    >
      {/* Table region */}
      <div
        style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        onClickCapture={handleGridClickCapture}
      >
        <DataGridView
          style={{ height: "100%" }}
          columns={columns}
          dataSource={pagedReferences}
          getCellSemantics={(column, record) => {
            if (column.dataIndex === "name") return { link: record?.link };
            return {};
          }}
          EmptyComponent="There are no references right now!"
        />
      </div>

      {/* Pagination footer */}
      <div
        style={{
          height: showFooter ? FOOTER_H : 0,
          display: showFooter ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Pagination
          current={safePageNo}
          pageSize={PER_PAGE}
          total={totalCount}
          showSizeChanger={false}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};
